import type { Ref } from 'vue'
import { onBeforeUnmount, watch } from 'vue'

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function randomPeriodSec(min: number, span: number): number {
  return min + Math.random() * span
}

interface ITravelBox {
  maxX: number
  maxY: number
}

/**
 * Smooth Lissajous drift (curved paths) for idle logo + status — avoids axis-aligned straight segments.
 */
export function useIdleScreensaverMotion(
  contentRef: Ref<HTMLElement | null>,
  boundsRef: Ref<HTMLElement | null>,
  enabled: Ref<boolean>
): void {
  let rafId = 0
  let resizeObserver: ResizeObserver | null = null
  let measureRaf = 0
  let travel: ITravelBox = { maxX: 0, maxY: 0 }
  let periodX = 30
  let periodY = 38
  let phaseY = 0
  let startMs = 0

  function pickWaveParams(): void {
    periodX = randomPeriodSec(38, 18)
    periodY = randomPeriodSec(46, 22)
    phaseY = Math.random() * Math.PI * 2
    startMs = performance.now()
  }

  function measureTravel(): void {
    const bounds = boundsRef.value
    const content = contentRef.value
    if (bounds == null || content == null) {
      return
    }
    travel = {
      maxX: Math.max(0, bounds.clientWidth - content.offsetWidth),
      maxY: Math.max(0, bounds.clientHeight - content.offsetHeight)
    }
  }

  function applyCentered() {
    const content = contentRef.value
    if (content == null) {
      return
    }
    const x = travel.maxX / 2
    const y = travel.maxY / 2
    content.style.transform = `translate3d(${x}px, ${y}px, 0)`
  }

  function tick(now: number) {
    const content = contentRef.value
    if (content == null || !enabled.value || prefersReducedMotion()) {
      stopLoop()
      return
    }

    const elapsed = (now - startMs) / 1000
    const wx = (2 * Math.PI * elapsed) / periodX
    const wy = (2 * Math.PI * elapsed) / periodY + phaseY
    // Lissajous + light harmonics → smooth curved paths (not axis-aligned straight segments).
    const xWave = 0.84 * Math.sin(wx) + 0.16 * Math.sin(wx * 2.13 + 0.55)
    const yWave = 0.84 * Math.sin(wy) + 0.16 * Math.sin(wy * 1.87 + 1.05)
    const x = (travel.maxX / 2) * (1 + xWave)
    const y = (travel.maxY / 2) * (1 + yWave)

    content.style.transform = `translate3d(${x}px, ${y}px, 0)`
    rafId = requestAnimationFrame(tick)
  }

  function startLoop() {
    if (prefersReducedMotion() || !enabled.value) {
      return
    }
    stopLoop()
    pickWaveParams()
    rafId = requestAnimationFrame(tick)
  }

  function stopLoop() {
    if (rafId !== 0) {
      cancelAnimationFrame(rafId)
      rafId = 0
    }
  }

  function scheduleMeasure() {
    if (measureRaf !== 0) {
      cancelAnimationFrame(measureRaf)
    }
    measureRaf = requestAnimationFrame(() => {
      measureRaf = 0
      measureTravel()
      if (!enabled.value || prefersReducedMotion()) {
        applyCentered()
        return
      }
      if (rafId === 0) {
        startLoop()
      }
    })
  }

  function bindResizeObserver() {
    resizeObserver?.disconnect()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const bounds = boundsRef.value
    const content = contentRef.value
    if (bounds == null || content == null) {
      return
    }
    resizeObserver = new ResizeObserver(() => {
      scheduleMeasure()
    })
    resizeObserver.observe(bounds)
    resizeObserver.observe(content)
  }

  function teardown() {
    stopLoop()
    if (measureRaf !== 0) {
      cancelAnimationFrame(measureRaf)
      measureRaf = 0
    }
    resizeObserver?.disconnect()
    resizeObserver = null
    const content = contentRef.value
    if (content != null && !enabled.value) {
      content.style.transform = ''
    }
  }

  watch(
    [enabled, contentRef, boundsRef],
    () => {
      if (enabled.value && contentRef.value != null && boundsRef.value != null) {
        bindResizeObserver()
        scheduleMeasure()
      }
      else {
        teardown()
        if (enabled.value && prefersReducedMotion()) {
          measureTravel()
          applyCentered()
        }
      }
    },
    { flush: 'post' }
  )

  onBeforeUnmount(teardown)
}
